import AddressPicker from "./AddressPicker";


import { useCallback, useEffect, useId, useMemo, useState, type ReactNode } from "react";
import { userAPI } from "../UserAPI";
import {
    ArrowDown,
    ArrowUp,
    ArrowUpDown,
    Columns3,
    Download,
    ChevronRight,
    Loader2,
    MapPinOff,
    QrCode,
    Trash2,
} from "lucide-react";
import type { Column, ColumnDef } from "@tanstack/react-table";
import {
    Asset,
    Building,
    EntityQuery,
    Floor,
    Product,
    Query,
    Site,
    and,
    from,
    is,
    or,
} from "@the-real-insight/tri-model";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ProductPageLink } from "./ProductPageLink";
import { QRCodeCanvas } from "qrcode.react";
import {
    pdf,
    Document,
    Page,
    Text as PDFText,
    Image,
    StyleSheet,
    View,
} from "@react-pdf/renderer";
import { Icon } from "./Icon";
import MaterialSymbol from "./MaterialSymbol";
import { resolveDin276Style } from "../utilities/din276Style";
import { useModalContext } from "./ModalProvider";
import { useUrlPageState } from "../utilities/dataTablePagination";
import { useAdministrationAPI } from "../AdministrationAPI";
import { assetListScopeFilters } from "./assetListScope";
import { AssetReadingCell, AssetStatusCell, readingSortValue } from "./AssetQuickView";
import { AssetStatusFilterBar } from "./AssetStatusBadge";
import { useAssetStatusSummary } from "../hooks/useAssetStatusSummary";
import {
  countStatuses,
  matchesStatusFilter,
  severityRank,
  type StatusFilter,
} from "../utilities/assetStatus";
import {
  describeAssetLocation,
  resolveAssetLocations,
  type AssetLocationParts,
} from "../utilities/assetLocation";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";

interface Properties {
  className?: string;
  product?: Product;
  processError?: (error: any) => void;
  scopeSites?: Site[];
  scopeBuildings?: Building[];
  scopeFloors?: Floor[];
  showOperatingState?: boolean;
  iotOnly?: boolean;
  assets?: Asset[];
  resultsCapped?: boolean;
  /** Only devices without a floor and without a space (#785) — the ones no floor plan shows. */
  unassignedOnly?: boolean;
  /** Keep devices decommissioned in the app; by default they belong to the archive (#780). */
  includeDecommissioned?: boolean;
  /** Add a column naming building · floor · space, resolved for the loaded page of devices. */
  showLocation?: boolean;
  /** What to say when nothing matches — defaults to "no equipment registered". */
  emptyMessage?: ReactNode;
  /**
   * Service view (tri-server#1364): status overview with filter chips, "Zustand" / "Druck" /
   * "Batterie" columns, sorted by severity, denser rows and a "Spalten" menu for the columns
   * a service round rarely needs. Status is loaded for every device of the list (the counts
   * cover all of them), in requests of at most 500 ids.
   */
  showStatus?: boolean;
}

/** Columns the service view hides by default; the "Spalten" menu brings them back. */
const STATUS_VIEW_HIDDEN_COLUMNS = ["photos", "lastModified", "installed"];
/** Columns that are always shown — the "Spalten" menu does not offer them. */
const FIXED_COLUMNS = new Set(["actions", "type", "nameId", "status"]);
/** Header label per hideable column, for the "Spalten" menu. */
const COLUMN_LABEL_KEYS: Record<string, string> = {
  pressure: "assetList.pressure",
  battery: "assetList.battery",
  product: "assetList.productManufacturer",
  address: "assetList.address",
  location: "assetList.location",
  photos: "assetList.photos",
  lastModified: "assetList.lastModified",
  installed: "assetList.installed",
  lastMaintained: "assetList.lastMaintained",
  operatingState: "assetList.operatingState",
};
/** Lower rows in the service view. */
const DENSE_ROWS = "[&>td]:py-1.5";
/** Tighter cell padding in the service view, header and body alike (DataTable applies `meta.className` to both). */
const DENSE_CELL = "h-9 px-2 first:pl-3 last:pr-3";

/** Klickbarer Spaltenkopf — ersetzt die `sortable`-Köpfe der abgelösten Tabelle. */
function SortableHeader({
  column,
  children,
  align,
  dense,
}: {
  column: Column<any, unknown>;
  children: ReactNode;
  align?: "right";
  /** Compact header for the service view (smaller text, tighter padding). */
  dense?: boolean;
}) {
  const sorted = column.getIsSorted();

  return (
    <div className={cn(align === "right" && "text-right")}>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={cn(
          "gap-1 font-bold text-foreground",
          dense
            ? cn("h-7 gap-0.5 whitespace-nowrap px-1.5 text-xs", align === "right" ? "-mr-1.5" : "-ml-1.5")
            : "-ml-3 h-8 px-3",
        )}
        onClick={() => column.toggleSorting(sorted === "asc")}
      >
        {children}
        {sorted === "asc" ? (
          <ArrowUp aria-hidden className="h-3.5 w-3.5" />
        ) : sorted === "desc" ? (
          <ArrowDown aria-hidden className="h-3.5 w-3.5" />
        ) : (
          <ArrowUpDown aria-hidden className="h-3.5 w-3.5 opacity-50" />
        )}
      </Button>
    </div>
  );
}

const PDFQRCodes = ({ qrCodes }: { qrCodes: any[] }) => {
  const styles = StyleSheet.create({
    page: {
      margin: "0.5cm",
      padding: 0,
    },
    qrCodes: {
      display: "flex",
      flexDirection: "row",
      flexWrap: "wrap",
    },
    qrCode: {
      border: "1px solid #efefef",
      width: "4cm",
      height: "4cm",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    },
    qrCodeImage: {
      width: "2.5cm",
      height: "2.5cm",
    },
    companyText: {
      marginTop: "0.2cm",
      marginBottom: "0.2cm",
      fontSize: 9,
    },
    idText: {
      marginTop: "0.2cm",
      fontSize: 6,
    },
  });

  try {
    return (
      <Document>
        <Page size="A4" style={styles.page}>
          <View style={styles.qrCodes}>
            {qrCodes.map((qrCode: any) => (
              <View key={qrCode.id} style={styles.qrCode}>
                <PDFText style={styles.companyText}>FireIQ</PDFText>
                <Image style={styles.qrCodeImage} src={qrCode.image} />
                <PDFText style={styles.idText}>{qrCode.id}</PDFText>
              </View>
            ))}
          </View>
        </Page>
      </Document>
    );
  } catch (error) {
    console.log(error);

    return (
      <Document>
        <Page>
          <PDFText>Error in Invoice Creation.</PDFText>
        </Page>
      </Document>
    );
  }
};

export default function AssetList(properties: Properties) {
  const { t, i18n } = useTranslation();
  const paginationProps = useUrlPageState('assetListPage');
  const navigate = useNavigate();
  const { showDeleteDialog, pushError: _pushError } = useModalContext() as any;
  const [quickFilter, setQuickFilter] = useState() as any;
  const [assets, setAssets] = useState([]) as any;
  const [address, setAddress] = useState() as any;
  // Names for the devices' floor/space ids, keyed by asset `_id`; only kept when the
  // location column is shown.
  const [locations, setLocations] = useState<Record<string, AssetLocationParts>>({});
  const [loading, setLoading] = useState(false) as any;
  // True when the DB query hit ASSET_QUERY_LIMIT and results were truncated —
  // the toolbar then hints the user to narrow the search.
  const [capped, setCapped] = useState(false) as any;
  const [downloading, setDownloading] = useState(false) as any;
  const { deleteAsset } = useAdministrationAPI();
  const quickFilterId = useId();
  const productFilterId = useId();

  // Status for every device of the list (tri-server#1364): the overview counts and the
  // severity sort need all of them, not just the visible page. The hook splits at 500 ids.
  const assetIds = useMemo(
    () => (assets as any[]).map((asset: any) => String(asset?._id ?? "")).filter(Boolean),
    [assets],
  );
  const {
    summaries: statusSummaries,
    loading: statusLoading,
    error: statusError,
  } = useAssetStatusSummary(assetIds, { enabled: !!properties.showStatus });
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const statusCounts = useMemo(
    () => countStatuses(assetIds, statusSummaries),
    [assetIds, statusSummaries],
  );
  // A new array whenever the statuses change: TanStack caches sort values per row, so new
  // rows are what makes the severity order follow the loaded statuses.
  const tableData = useMemo(
    () =>
      properties.showStatus
        ? (assets as any[]).filter((asset: any) =>
            matchesStatusFilter(statusSummaries[String(asset?._id ?? "")], statusFilter),
          )
        : assets,
    [assets, properties.showStatus, statusSummaries, statusFilter],
  );
  const { onPageChange: onTablePageChange } = paginationProps;
  const changeStatusFilter = useCallback(
    (filter: StatusFilter) => {
      setStatusFilter(filter);
      onTablePageChange(1);
    },
    [onTablePageChange],
  );
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(STATUS_VIEW_HIDDEN_COLUMNS.map((id) => [id, false])),
  );
  const summaryOf = (asset: any) => statusSummaries[String(asset?._id ?? "")];

  // The service view packs the table: one-line cells, truncated text with the full value as
  // tooltip, so it fits a 1440 px screen without scrolling sideways.
  const dense = !!properties.showStatus;

  const assetColumns: ColumnDef<any, any>[] = [
    {
      id: "actions",
      header: "",
      size: dense ? 64 : 80,
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex items-center">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={cn(dense && "h-7 w-7")}
            aria-label={t("entityPage.openDevice")}
            title={t("entityPage.openDevice")}
            onClick={() => {
              navigate(
                `/entity?selectedEntityType=Asset&selectedEntity=${row.original._id}`
              );
            }}
          >
            <ChevronRight aria-hidden="true" className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={cn("text-destructive", dense && "h-7 w-7")}
            aria-label={t("assetList.deleteAsset")}
            onClick={async () => {
              showDeleteDialog(t("assetList.deleteAsset"), row.original.id, async () => {
                await deleteAsset(row.original);

                refreshAssets();
              });
            }}
          >
            <Trash2 aria-hidden="true" className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
    {
      id: "type",
      header: ({ column }) => <SortableHeader column={column} dense={dense}>{t("assetList.type")}</SortableHeader>,
      size: dense ? 190 : 250,
      accessorFn: (asset: any) => asset.schemaCategoryName,
      cell: ({ row }) => (
        <div className={cn("flex min-w-0 items-center", dense ? "gap-2" : "gap-4")}>
          <div className="flex min-w-[24px] items-center justify-center">
            {row.original.categoryIcon ? (
              <MaterialSymbol
                name={row.original.categoryIcon}
                color={row.original.categoryColor}
                size={24}
                title={row.original.schemaCategoryName}
              />
            ) : row.original.icon ? (
              <Icon id={row.original.icon}></Icon>
            ) : (
              <></>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-xs">{row.original.schemaCategory}</div>
            <div
              className={cn("truncate text-xs", dense ? "max-w-[140px]" : "max-w-[190px]")}
              title={row.original.schemaCategoryName}
            >
              {row.original.schemaCategoryName}
            </div>
          </div>
        </div>
      ),
    },
    {
      id: "nameId",
      header: ({ column }) => <SortableHeader column={column} dense={dense}>{t("assetList.nameId")}</SortableHeader>,
      size: dense ? 150 : 200,
      accessorFn: (asset: any) => asset.name || asset.id,
      cell: ({ row }) => (
        <div className="min-w-0 break-words text-xs">{row.original.name || row.original.id}</div>
      ),
    },
    {
      id: "product",
      header: ({ column }) => <SortableHeader column={column} dense={dense}>{t("assetList.productManufacturer")}</SortableHeader>,
      size: dense ? 160 : 200,
      accessorFn: (asset: any) => asset.productName,
      cell: ({ row }) =>
        dense ? (
          <div className="min-w-0 max-w-[160px]" title={[row.original.productName, row.original.manufacturerName].filter(Boolean).join(" · ")}>
            <div className="truncate text-xs">
              <ProductPageLink product={row.original.product}>{row.original.productName}</ProductPageLink>
            </div>
            <div className="truncate text-[11px] text-muted-foreground">{row.original.manufacturerName}</div>
          </div>
        ) : (
          <div className="min-w-0">
            <div className="break-words text-xs">
              <ProductPageLink product={row.original.product}>{row.original.productName}</ProductPageLink>
            </div>
            <div className="break-words text-xs">{row.original.manufacturerName}</div>
          </div>
        ),
    },
    {
      id: "address",
      header: ({ column }) => <SortableHeader column={column} dense={dense}>{t("assetList.address")}</SortableHeader>,
      size: dense ? 180 : 200,
      accessorFn: (asset: any) =>
        `${asset.street} ${asset.streetNumber}, ${asset.city}`,
      cell: ({ row }) => {
        if (dense) {
          // One line: street and number in full, the city truncated; the tooltip has it all.
          const street = [row.original.street, row.original.streetNumber].filter(Boolean).join(" ");
          const city = [row.original.postalCode, row.original.city].filter(Boolean).join(" ");
          return (
            <div
              data-testid="asset-address"
              className="flex min-w-0 max-w-[180px] items-baseline gap-1 whitespace-nowrap text-xs"
              title={[street, city].filter(Boolean).join(", ")}
            >
              <span className="shrink-0">{street}</span>
              {row.original.city ? (
                <span className="min-w-0 truncate text-muted-foreground">{row.original.city}</span>
              ) : null}
            </div>
          );
        }
        return (
        <div className="min-w-0">
          <div className="break-words text-xs">
            {row.original.street} {row.original.streetNumber}
          </div>
          <div className="break-words text-xs">
            {row.original.postalCode} {row.original.city}
          </div>
        </div>
        );
      },
    },
    {
      id: "photos",
      header: ({ column }) => <SortableHeader column={column} align="right" dense={dense}>{t("assetList.photos")}</SortableHeader>,
      size: 80,
      accessorFn: (asset: any) => (asset.photos ? asset.photos.length : 0),
      cell: ({ row }) => (
        <div className="text-right text-xs">{row.original.photos ? row.original.photos.length : 0}</div>
      ),
    },
    {
      id: "lastModified",
      header: ({ column }) => <SortableHeader column={column} align="right" dense={dense}>{t("assetList.lastModified")}</SortableHeader>,
      size: 100,
      accessorFn: (asset: any) =>
        asset.lastModified ? new Date(asset.lastModified).getTime() : 0,
      cell: ({ row }) => (
        <div className="text-right text-xs">
          {row.original.lastModified
            ? new Date(row.original.lastModified).toLocaleDateString(i18n.language)
            : "-"}
        </div>
      ),
    },
    {
      id: "installed",
      header: ({ column }) => <SortableHeader column={column} align="right" dense={dense}>{t("assetList.installed")}</SortableHeader>,
      size: 100,
      accessorFn: (asset: any) =>
        asset.installed ? new Date(asset.installed).getTime() : 0,
      cell: ({ row }) => (
        <div className="text-right text-xs">
          {row.original.installed
            ? new Date(row.original.installed).toLocaleDateString(i18n.language)
            : "-"}
        </div>
      ),
    },
    {
      id: "lastMaintained",
      header: ({ column }) => <SortableHeader column={column} align="right" dense={dense}>{t("assetList.lastMaintained")}</SortableHeader>,
      size: dense ? 96 : 100,
      accessorFn: (asset: any) =>
        asset.lastMaintained ? new Date(asset.lastMaintained).getTime() : 0,
      cell: ({ row }) => (
        <div className="text-right text-xs">
          {row.original.lastMaintained
            ? new Date(row.original.lastMaintained).toLocaleDateString(i18n.language)
            : "-"}
        </div>
      ),
    },
  ];

  if (properties.showLocation) {
    const locationOf = (asset: any) =>
      describeAssetLocation(asset, locations[String(asset._id)]);

    // Right after the address: the address says where the site is, this says where in it.
    assetColumns.splice(assetColumns.findIndex((column) => column.id === "address") + 1, 0, {
      id: "location",
      header: ({ column }) => <SortableHeader column={column} dense={dense}>{t("assetList.location")}</SortableHeader>,
      size: 220,
      accessorFn: (asset: any) => locationOf(asset) ?? "",
      cell: ({ row }) => {
        const location = locationOf(row.original);

        return location ? (
          <div className="min-w-0 break-words text-xs">{location}</div>
        ) : (
          <div className="inline-flex items-center gap-1 text-xs text-muted-foreground">
            <MapPinOff aria-hidden="true" className="h-3.5 w-3.5" />
            {t("assetList.unassigned")}
          </div>
        );
      },
    });
  }

  if (properties.showStatus) {
    // Right after the name: status is what the eye looks for first when servicing a site.
    // Pressure and battery follow as narrow number columns; temperature is in the badge tooltip.
    const nameIndex = assetColumns.findIndex((column) => column.id === "nameId");
    const readingColumn = (id: "pressure" | "battery", label: string): ColumnDef<any, any> => ({
      id,
      header: ({ column }) => (
        <SortableHeader column={column} align="right" dense={dense}>
          {label}
        </SortableHeader>
      ),
      size: 76,
      accessorFn: (asset: any) => readingSortValue(summaryOf(asset), id),
      sortUndefined: "last",
      cell: ({ row }) => (
        <AssetReadingCell summary={summaryOf(row.original)} column={id} loading={statusLoading} />
      ),
    });
    assetColumns.splice(
      nameIndex + 1,
      0,
      {
        id: "status",
        header: ({ column }) => <SortableHeader column={column} dense={dense}>{t("assetList.status")}</SortableHeader>,
        size: 140,
        // Severity first (critical > warning > offline/stale > maintenance > no data > normal >
        // decommissioned), the device id breaks ties.
        accessorFn: (asset: any) => severityRank(summaryOf(asset)),
        sortingFn: (a, b) =>
          severityRank(summaryOf(a.original)) - severityRank(summaryOf(b.original)) ||
          String(a.original.id ?? "").localeCompare(String(b.original.id ?? ""), undefined, { numeric: true }),
        cell: ({ row }) => <AssetStatusCell summary={summaryOf(row.original)} loading={statusLoading} />,
      },
      readingColumn("pressure", t("assetList.pressure")),
      readingColumn("battery", t("assetList.battery")),
    );
  }

  if (dense) {
    assetColumns.forEach((column) => {
      const meta = (column.meta ?? {}) as { className?: string };
      column.meta = { ...meta, className: cn(DENSE_CELL, meta.className) };
    });
  }

  if (properties.showOperatingState) {
    assetColumns.push({
      id: "operatingState",
      header: ({ column }) => <SortableHeader column={column} dense={dense}>{t("assetList.operatingState")}</SortableHeader>,
      size: 200,
      accessorFn: (asset: any) => asset.operationalState,
      cell: () => (
        <div className="min-w-0 break-words text-xs">
          <span className="font-bold text-success">
            {t("assetList.normal")}
          </span>
        </div>
      ),
    });
  }

  const calculateFilter = () => {
    const filters = [];

    // Exclude substructure parts (sub-assets). They are first-class Assets in the DB
    // but carry a `parentId` and are managed inside their parent's part hierarchy —
    // they must not appear as standalone rows in site/space/floor asset lists.
    // `parentId == null` selects top-level assets only: in MongoDB `{$eq: null}`
    // matches documents where the field is null OR absent. Applied server-side, so it
    // works regardless of whether the field is registered on the client tri-model build.
    filters.push(is("parentId").equal(null as any));

    // Archived devices (#780) live in Betrieb → Anlagen-Archiv, not in any operational list —
    // a site's assets are read by address, which the archive keeps, so the floor scope alone
    // would not hide them here. "Unassigned only" narrows to devices no floor plan can show.
    filters.push(
      ...assetListScopeFilters({
        unassignedOnly: properties.unassignedOnly,
        includeDecommissioned: properties.includeDecommissioned,
      })
    );

    console.log("Quick filter >>>", quickFilter);

    if (quickFilter && quickFilter.trim().length > 0) {
      filters.push(
        or(
          is("street").startsWith(quickFilter),
          is("city").startsWith(quickFilter),
          is("name").startsWith(quickFilter),
          is("productName").startsWith(quickFilter)
        )
      );
    }

    if (address) {
      // street + number + postalCode identify the address; city naming varies
      // between sources (geocoder "Frankfurt" vs ingested "Frankfurt am Main"),
      // so it is deliberately not matched — same reasoning as country below.
      filters.push(is("street").equal(address.street));
      filters.push(is("streetNumber").equal(address.streetNumber));
      filters.push(is("postalCode").equal(address.postalCode));
    }

    if (properties.product && properties.product._id) {
      filters.push(is("product").equal(properties.product._id));
    }

    if (properties.scopeSites) {
      console.log("Scope sites >>>", properties.scopeSites);

      properties.scopeSites.forEach((site: any) => {
        filters.push(
          and(
            is("street").equal(site.street),
            is("streetNumber").equal(site.streetNumber),
            is("postalCode").equal(site.postalCode)
            // city/country deliberately unmatched: naming varies between
            // sources ("Frankfurt" vs "Frankfurt am Main"); PLZ disambiguates.
          )
        );
      });
    }

    if (properties.scopeBuildings) {
      console.log("Scope buildings >>>", properties.scopeBuildings);

      properties.scopeBuildings.forEach((building: any) => {
        filters.push(
          and(
            is("street").equal(building.street),
            is("streetNumber").equal(building.streetNumber),
            is("postalCode").equal(building.postalCode)
            // city/country deliberately unmatched — see scopeSites above.
          )
        );
      });
    }

    if (properties.scopeFloors) {
      console.log("Scope floors >>>", properties.scopeFloors);

      properties.scopeFloors.forEach((floor: any) => {
        filters.push(is("floor").equal(floor._id));
      });
    }

    return and(...filters);
  };

  const enrichAssetsWithMetadata = async (assetsToEnrich: Asset[]) => {
    const metadata = (await userAPI.getDIN276Metadata()).reduce(
      (map: any, category: any) => {
        map[category.code] = category;

        return map;
      },
      {}
    );

    return assetsToEnrich.map((asset: any) => {
      const category = metadata[asset.schemaCategory];
      // DIN 276 category icon + colour — prefer the metadata (DB) value, fall back to the group
      // resolver so codes not present as Taxonomy nodes (e.g. 474.7) still get coded.
      const style = resolveDin276Style(asset.schemaCategory);
      return {
        ...asset,
        schemaCategoryName: category && category.name ? category.name : "-",
        categoryColor: (category && category.color) || style.color,
        categoryIcon: (category && category.icon) || style.icon,
      };
    });
  };

  // The names behind the devices' floor/space ids — only when they are shown, and never
  // at the expense of the list itself: a failed lookup leaves the ids in place.
  const resolveLocations = async (loaded: any[]): Promise<Record<string, AssetLocationParts>> =>
    properties.showLocation ? resolveAssetLocations(loaded).catch(() => ({})) : {};

  const refreshAssets = async () => {
    // If assets are provided via properties, use them directly
    if (properties.assets) {
      try {
        setLoading(true);
        setCapped(properties.resultsCapped ?? false);

        let result = await enrichAssetsWithMetadata(properties.assets);

        // Apply iotOnly filter if specified
        if (properties.iotOnly) {
          result = result.filter((asset: any) => asset.iotDeviceType);
        }

        console.log("Assets (from props) >>>", result);

        setLocations(await resolveLocations(result));
        setAssets(result);
      } catch (error) {
        console.error("Error enriching provided assets:", error);
      } finally {
        setLoading(false);
      }
      return;
    }

    // Otherwise, query from database
    try {
      const metadata = (await userAPI.getDIN276Metadata()).reduce(
        (map: any, category: any) => {
          map[category.code] = category;

          return map;
        },
        {}
      );

      setLoading(true);

      // Cap the query: a single product can own 100k+ assets, and pulling the
      // whole set hangs both the response and the client render. Users narrow
      // with the quick filter (which is pushed into the server-side query).
      const ASSET_QUERY_LIMIT = 2000;
      // Request one extra to detect truncation: if we get 2001 back, the set
      // was capped; if we get ≤2000, we have the complete result.
      const raw = await userAPI.query(
        from(Asset.type).where(calculateFilter()),
        0,
        ASSET_QUERY_LIMIT + 1,
      );
      setCapped(raw.length > ASSET_QUERY_LIMIT);
      // Trim to the display limit when capped.
      const limited = raw.slice(0, ASSET_QUERY_LIMIT);

      const result = limited
        .filter((asset: any) => {
          return (
            !properties.iotOnly || (properties.iotOnly && asset.iotDeviceType)
          );
        })
        .map((asset: any) => {
          const category = metadata[asset.schemaCategory];
          const style = resolveDin276Style(asset.schemaCategory);
          return {
            ...asset,
            schemaCategoryName: category && category.name ? category.name : "-",
            categoryColor: (category && category.color) || style.color,
            categoryIcon: (category && category.icon) || style.icon,
          };
        });

      console.log("Assets >>>", result);

      setLocations(await resolveLocations(result));
      setAssets(result);
    } catch (error) {
      console.error("Error refreshing assets:", error);
    } finally {
      setLoading(false);
    }
  };

  // Content-based refresh key: parents often pass fresh array literals on
  // every render (e.g. scopeSites={[selectedSite]}), and identity-based deps
  // then refetch — and flicker — on unrelated re-renders such as a paging
  // click that writes the page number to the URL.
  const refreshKey = useMemo(
    () =>
      JSON.stringify({
        product: properties.product?._id ?? null,
        sites: (properties.scopeSites || []).map(
          (site: any) => String(site._id ?? `${site.street}|${site.streetNumber}|${site.postalCode}`),
        ),
        buildings: (properties.scopeBuildings || []).map(
          (building: any) =>
            String(building._id ?? `${building.street}|${building.streetNumber}|${building.postalCode}`),
        ),
        floors: (properties.scopeFloors || []).map((floor: any) => String(floor._id ?? floor.name ?? "")),
        providedAssets: (properties.assets || []).map((asset: any) => String(asset._id ?? asset.id ?? "")),
        iotOnly: !!properties.iotOnly,
        quickFilter,
        address: address ? `${address.street}|${address.streetNumber}|${address.postalCode}` : null,
        resultsCapped: properties.resultsCapped ?? false,
        unassignedOnly: !!properties.unassignedOnly,
        includeDecommissioned: !!properties.includeDecommissioned,
        showLocation: !!properties.showLocation,
      }),
    [
      properties.product,
      properties.scopeSites,
      properties.scopeBuildings,
      properties.scopeFloors,
      properties.assets,
      properties.iotOnly,
      quickFilter,
      address,
      properties.resultsCapped,
      properties.unassignedOnly,
      properties.includeDecommissioned,
      properties.showLocation,
    ],
  );

  useEffect(() => {
    refreshAssets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const downloadFile = (blob: Blob, fileName: string) => {
    const link = document.createElement("a");

    link.href = URL.createObjectURL(blob);
    link.download = fileName;

    // Some browser needs the anchor to be in the doc

    document.body.append(link);
    link.click();
    link.remove();

    // In case the Blob uses a lot of memory

    setTimeout(() => URL.revokeObjectURL(link.href), 7000);
  };

  return (
    <TooltipProvider>
      <div className={cn("flex min-w-0 flex-col gap-4", properties.className)}>
        {/* Top-aligned: the address picker reserves a line below its field for the chosen address, so
            bottom alignment pushed the other fields below its input (14.10.2026). */}
        <div className="flex min-w-0 flex-wrap items-start gap-x-6 gap-y-4">
          <div className="flex min-w-0 flex-col gap-1.5 max-sm:w-full">
            <Label htmlFor={quickFilterId}>{t("assetList.quickFilter")}</Label>
            <Input
              id={quickFilterId}
              className="w-[180px] max-w-full max-sm:w-full"
              placeholder={t("assetList.enterFilter")}
              value={quickFilter ?? ""}
              onChange={(event) => setQuickFilter(event.target.value)}
            />
          </div>
          {!properties.product || !properties.product._id ? (
            /* Bestand: dieses Feld hat keinen Handler und filtert nichts. */
            <div className="flex min-w-0 flex-col gap-1.5 max-sm:w-full">
              <Label htmlFor={productFilterId}>{t("assetList.product")}</Label>
              <Input
                id={productFilterId}
                className="w-[300px] max-w-full max-sm:w-full"
                placeholder={t("assetList.enterProductName")}
                value={""}
                onChange={() => {}}
              />
            </div>
          ) : (
            <></>
          )}
          {properties.scopeBuildings || properties.scopeFloors ? (
            <></>
          ) : (
            <div className="min-w-0 max-sm:w-full">
              <AddressPicker
                label={t("assetList.address")}
                setAddress={setAddress}
              />
            </div>
          )}
          {/* pt-5: label height plus gap — the icons sit on the line of the inputs. */}
          <div className="flex flex-1 flex-wrap items-center justify-end gap-4 sm:pt-5">
            {downloading ? (
              <div className="flex items-center gap-2">
                <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />
                <div className="text-xs">{t("global.downloading")}</div>
              </div>
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={t("assetList.downloadZip")}
                    onClick={async () => {
                      setDownloading(true);

                      try {
                        const blob = await userAPI.queryZip(
                          new Query(
                            new EntityQuery(
                              Asset.type,
                              [],
                              undefined,
                              calculateFilter()
                            )
                          )
                        );

                        downloadFile(
                          blob,
                          `asset-data-${new Date().toISOString()}.zip`
                        );
                      } finally {
                        setDownloading(false);
                      }
                    }}
                  >
                    <Download aria-hidden="true" className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t("assetList.downloadZip")}</TooltipContent>
              </Tooltip>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={t("assetList.downloadQrCodes")}
                  onClick={async () => {
                    const qrCodes: any = [];

                    for (const asset of assets) {
                      const canvasDiv = document.getElementById(
                        `qrCanvas-${asset.id}`
                      );

                      console.log(canvasDiv);

                      if (!canvasDiv) {
                        throw new Error("Cannot find canvas container");
                      }

                      const canvas = canvasDiv.querySelector("canvas");

                      console.log(canvas);

                      if (!canvas) {
                        throw new Error("Cannot find canvas");
                      }

                      qrCodes.push({
                        image: canvas.toDataURL("image/jpg", 0.3),
                        id: asset.id,
                      });
                    }

                    const blob = await pdf(
                      <PDFQRCodes qrCodes={qrCodes}></PDFQRCodes>
                    ).toBlob();

                    console.log(blob);

                    downloadFile(blob, `QR Codes`);
                  }}
                >
                  <QrCode aria-hidden="true" className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t("assetList.downloadQrCodes")}</TooltipContent>
            </Tooltip>
          </div>
        </div>
        {capped && !loading && (
          <div className="text-xs text-warning">
            {t("assetList.resultsCapped")}
          </div>
        )}
        {properties.showStatus ? (
          <div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
            {assetIds.length > 0 ? (
              <AssetStatusFilterBar
                counts={statusCounts}
                value={statusFilter}
                onChange={changeStatusFilter}
                loading={statusLoading}
                error={!!statusError}
              />
            ) : (
              <span />
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button" variant="ghost" size="sm" className="h-7 gap-1.5 px-2 text-xs">
                  <Columns3 aria-hidden="true" className="h-3.5 w-3.5" />
                  {t("assetList.columns")}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel className="text-xs">{t("assetList.columnsLabel")}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {assetColumns
                  .filter((column) => column.id && !FIXED_COLUMNS.has(column.id))
                  .map((column) => {
                    const id = column.id as string;
                    return (
                      <DropdownMenuCheckboxItem
                        key={id}
                        className="text-xs"
                        checked={columnVisibility[id] !== false}
                        onSelect={(event) => event.preventDefault()}
                        onCheckedChange={(checked) =>
                          setColumnVisibility((current) => ({ ...current, [id]: !!checked }))
                        }
                      >
                        {t(COLUMN_LABEL_KEYS[id] ?? id)}
                      </DropdownMenuCheckboxItem>
                    );
                  })}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ) : null}
        <div className={cn(properties.showStatus ? "min-w-0" : "overflow-x-auto")}>
          <DataTable
            columns={assetColumns}
            data={loading ? [] : tableData}
            loading={loading}
            pageSize={10}
            {...paginationProps}
            emptyMessage={
              properties.showStatus && statusFilter !== "all" && assets.length > 0
                ? t("assetList.filteredEmpty")
                : properties.emptyMessage ?? t("assetList.noEquipment")
            }
            {...(properties.showStatus
              ? {
                  initialSorting: [{ id: "status", desc: false }],
                  columnVisibility,
                  rowClassName: () => DENSE_ROWS,
                }
              : {})}
          />
        </div>
        {/* Unsichtbare Render-Fläche für den QR-Export: auf 0x0 geklemmt, damit
            die Canvas-Reihe (bei vielen Anlagen mehrere tausend Pixel) keinen
            horizontalen Scrollraum im Panel erzeugt. Canvas-Zeichnung und
            Download (getElementById) funktionieren unabhängig vom Layout. */}
        <div className="qrRenderer h-0 w-0 overflow-hidden">
          {assets.map((asset: Asset) => (
            <div key={asset.id} id={`qrCanvas-${asset.id}`}>
              <QRCodeCanvas
                value={`https://the-real-insight.ai/public/assets/${asset.id}`}
              />
            </div>
          ))}
        </div>
      </div>
    </TooltipProvider>
  );
}
